import React, {memo, Suspense, useState, useEffect} from "react"
import {MANIFEST} from "@manifest/header"
import {breakpoints} from "@next/ux-fabric"
import {useTemplatedComponent} from "@next/templating"
import {useMediaQuery} from "../../hooks/useMediaQuery"
import StaticBannerInnerContainer from "../StaticBannerInnerContainer"
import connect from "./connect"
import ContentBanner from "../ContentBanner"
import MessageBanner from "../MessageBanner"
import {QuickLinkProps} from "../QuickLink"
import {
    BannerContainer,
    QuickLinksContainer,
    QuickLinkContainer,
    QuickLinkDivider,
    BannerPromoText,
    MessageBannerWrapper,
} from "./components"
import QuickLinkBannerLink from "../QuickLinkBannerLink"
import StaticHeaderBannerContentWrapper from "../StaticHeaderBannerContentWrapper"

export type StaticHeaderBannerProps = {
    quickLinks: QuickLinkProps[] | undefined
    enableContentBanner: boolean
    isContentBannerExcluded: boolean
    templating: {
        mainTemplate: string
        fallbackTemplate: string
    }
    showMessageBanner: boolean
    messageBannerBackgroundColor: string
    isCountrySelectorInGlobalAppBar: boolean
    xxlBreakpointEnabled: boolean
}

export const StaticHeaderBanner = memo(
    ({
        quickLinks,
        enableContentBanner,
        isContentBannerExcluded,
        templating,
        showMessageBanner,
        messageBannerBackgroundColor,
        isCountrySelectorInGlobalAppBar,
        xxlBreakpointEnabled,
    }: StaticHeaderBannerProps) => {
        const isContentBannerEnabled = enableContentBanner && !isContentBannerExcluded

        // SSR-safe media query: defer evaluation until after hydration to prevent mismatch
        const [isHydrated, setIsHydrated] = useState(false)
        useEffect(() => {
            setIsHydrated(true)
        }, [])

        // Always call the hook, but use hydration state to determine the final value
        const mediaQueryResult = useMediaQuery(`(min-width:${breakpoints.values.md.toString()}px)`)
        // During SSR and before hydration, assume desktop layout to maintain consistency
        const isMedUp = isHydrated ? mediaQueryResult : true

        const showStaticBanner =
            isContentBannerEnabled ||
            ((quickLinks?.length ?? 0) > 0 && isMedUp) ||
            showMessageBanner ||
            isCountrySelectorInGlobalAppBar
        const TemplatedBannerInnerContainer = useTemplatedComponent(templating, MANIFEST)

        return showStaticBanner ? (
            <BannerContainer
                data-testid="header-staticheaderbanner"
                showStaticBanner={showStaticBanner}
                backgroundColor={messageBannerBackgroundColor}
            >
                {showMessageBanner && (
                    <MessageBannerWrapper xxlBreakpointEnabled={xxlBreakpointEnabled}>
                        <MessageBanner />
                    </MessageBannerWrapper>
                )}
                <StaticHeaderBannerContentWrapper
                    quickLinkCount={quickLinks?.length || 0}
                    enableContentBanner={isContentBannerEnabled}
                >
                    {isContentBannerEnabled && !showMessageBanner ? (
                        <Suspense>
                            <TemplatedBannerInnerContainer>
                                <ContentBanner />
                            </TemplatedBannerInnerContainer>
                        </Suspense>
                    ) : (
                        <div />
                    )}
                    <StaticBannerInnerContainer>
                        <BannerPromoText id="mt-vision-header-ribbon" />
                        {(quickLinks?.length ?? 0) > 0 && (
                            <QuickLinksContainer>
                                {quickLinks?.map((quickLink, index) => (
                                    <React.Fragment key={quickLink.text}>
                                        <QuickLinkContainer>
                                            <QuickLinkBannerLink
                                                href={quickLink.url}
                                                title={quickLink.accessibilityText}
                                            >
                                                {quickLink.text}
                                            </QuickLinkBannerLink>
                                        </QuickLinkContainer>
                                        {index !== quickLinks.length - 1 && <QuickLinkDivider orientation="vertical" />}
                                    </React.Fragment>
                                ))}
                            </QuickLinksContainer>
                        )}
                    </StaticBannerInnerContainer>
                </StaticHeaderBannerContentWrapper>
            </BannerContainer>
        ) : null
    },
)
StaticHeaderBanner.displayName = "StaticHeaderBanner"

export default connect(StaticHeaderBanner)
